import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { TimeOffApprovalFlow } from '@app/models/time-off-v3/time-off-approval-flow.model';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffRequestRules } from '@app/models/time-off-v3/time-off-request-rules.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { AdvancedPolicyForm, PolicyForm } from '@app/modules/time-off-v3/forms';
import { AnalyticService, NotifyService } from '@app/services';
import { BaseForm } from '@forms/base.form';

@Component({
    templateUrl: './policy-store.template.html',
    styleUrls: ['./policy-store.style.scss'],
})
export class PolicyStoreView implements OnInit {
    @ViewChild('advancePolicyForm') advancePolicyForm: AdvancedPolicyForm;
    @ViewChild('policyForm') policyForm: PolicyForm;

    isLoading = false;
    // These properties are required to have defaults
    timeOffPolicy: TimeOffPolicy = new TimeOffPolicy({
        proratesFirstAccrual: false,
        accrualFromHireDate: false,
        carriesOverOnHireDate: false,
        isUnlimited: true,
    });
    timeOffTypes: TimeOffType[] = [];
    timeOffApprovalFlows: TimeOffApprovalFlow[] = [];
    requestRules = new TimeOffRequestRules();

    constructor(
        private notify: NotifyService,
        private router: Router,
        private route: ActivatedRoute,
        private analyticService: AnalyticService
    ) {
        this.requestRules.timeOffPolicy = this.timeOffPolicy;
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        await this.loadTimeOffTypes();
        await this.loadTimeOffApprovalFlows();

        this.isLoading = false;
    }

    async onSave(): Promise<void> {
        if (!this.submitAndValidate(this.policyForm) || !this.submitAndValidate(this.advancePolicyForm)) {
            return;
        }

        try {
            this.isLoading = true;

            this.timeOffPolicy.timeOffSeniorityOverridesPayload = this.advancePolicyForm?.timeOffSeniorityOverrides.map(
                (seniorityOverride) => seniorityOverride.toPayload()
            );

            this.timeOffPolicy = await this.timeOffPolicy.save();

            this.analyticService.trackEvent(AnalyticEvents.TimeOffTypesAndPoliciesCreatingPolicyAndSaving);

            if (this.timeOffPolicy.isUnlimited) {
                this.isLoading = false;
                this.notify.success('time-off-v3.timeOffPolicyHasBeenCreated');
                this.redirect();
                return;
            }

            await this.saveAdvancedSettings();
            this.notify.success('time-off-v3.timeOffPolicyHasBeenCreated');
            this.redirect();
        } catch (err) {
            this.isLoading = false;
            this.notify.error(err);
        }
    }

    async saveAdvancedSettings(): Promise<void> {
        const requestRules: TimeOffRequestRules = this.advancePolicyForm.requestRules;

        /*
         * Make the call for the requestRules with the returned id from the Time Off Policy.
         * As well only make the call when the requestRules object actually has values from the form other than id.
         */
        if (requestRules.isDirty()) {
            requestRules.id = this.timeOffPolicy.timeOffRequestRule.id;
            await requestRules.save();
        }
    }

    submitAndValidate(form: BaseForm): boolean {
        if (form) {
            form.submit();
            return form.valid;
        }
        return true;
    }

    onCancel(): void {
        this.redirect();
    }

    redirect(): void {
        this.router.navigate(['/time-off/v3/types-and-policies'], { relativeTo: this.route });
    }

    async loadTimeOffTypes(): Promise<void> {
        try {
            this.isLoading = true;
            const [timeOffTypes] = await TimeOffType.all();
            this.timeOffTypes = timeOffTypes;
            this.timeOffPolicy.timeOffType = this.timeOffTypes.find(
                (timeOffType) => timeOffType.id === this.timeOffPolicy.timeOffTypeId
            );
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }
    }

    async loadTimeOffApprovalFlows(): Promise<void> {
        try {
            this.isLoading = true;
            const [timeOffApprovalFlows] = await TimeOffApprovalFlow.get();
            this.timeOffApprovalFlows = timeOffApprovalFlows;
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }
    }

    showAdvanceSetting(): boolean {
        // If the user has not selected unlimited as the accrual and if the user has selected a time off type then show the advance settings.
        if (
            this.timeOffPolicy.isUnlimited !== undefined &&
            !this.timeOffPolicy.isUnlimited &&
            this.timeOffPolicy.timeOffTypeId
        ) {
            return true;
        }

        return false;
    }
}
